import { useState, type FormEvent } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormField,
  NotificationCard,
  useToast,
} from '@sighthost/ui';
import { siteConfig } from '@sighthost/config';
import { supabase } from '@/shared/lib/supabase';

/**
 * RegisterDomainModal — admin-on-behalf-of domain registration.
 *
 * Calls the extended domain-register-confirm EF with `acting_for_customer_id`.
 * Issues an invoice the customer pays via PayFast; the registrar mutation
 * runs post-payment via the existing saga.
 *
 * .co.za / .org.za / .net.za / .web.za only (per AD-23 lock — gTLD reseller
 * coverage not yet confirmed).
 */

interface RegistrantInput {
  name: string;
  email: string;
  contactNumber: string;
  address1: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  organisation?: string;
}

interface Props {
  customerId: string;
  customerEmail: string;
  onClose: () => void;
  onSubmitted: (domain: string) => void;
}

export function RegisterDomainModal({ customerId, customerEmail, onClose, onSubmitted }: Props) {
  const { toast } = useToast();
  const [domain, setDomain] = useState('');
  const [periodYears, setPeriodYears] = useState(1);
  const [bindToCpanel, setBindToCpanel] = useState('');
  // Defaults: customer's email keeps them as the legal registrant. Phone +
  // physical address fall back to SightHost's own (agent-of-record pattern).
  // Name stays empty so admin must enter the customer's legal name.
  const [registrant, setRegistrant] = useState<RegistrantInput>({
    name: '',
    email: customerEmail,
    contactNumber: siteConfig.phone,
    address1: siteConfig.address.street,
    city: siteConfig.address.city,
    province: siteConfig.address.region,
    postalCode: siteConfig.address.postalCode,
    country: siteConfig.address.country,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateRegistrant<K extends keyof RegistrantInput>(key: K, value: RegistrantInput[K]) {
    setRegistrant((r) => ({ ...r, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!domain.trim()) {
      setError('Domain name is required.');
      return;
    }
    const missing = (['name', 'email', 'contactNumber', 'address1', 'city', 'province', 'postalCode', 'country'] as const)
      .filter((k) => !registrant[k]?.trim());
    if (missing.length > 0) {
      setError(`Registrant fields missing: ${missing.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: efErr } = await supabase.functions.invoke('domain-register-confirm', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: {
          domain: domain.trim().toLowerCase(),
          period_years: periodYears,
          bind_to_cpanel_username: bindToCpanel.trim() || undefined,
          registrant,
          acting_for_customer_id: customerId,
        },
      });
      if (efErr) throw new Error(efErr.message ?? 'Edge function call failed');
      const result = data as { ok: true; domain: string; invoice_number: string };
      toast({
        tone: 'success',
        title: 'Domain order created',
        body: `Invoice ${result.invoice_number} issued for ${result.domain}.`,
      });
      onSubmitted(result.domain);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Register domain on behalf"
      description="The customer will receive an emailed invoice + PayFast link. Registrar runs after payment clears."
      size="wide"
    >
      <ModalBody>
        <form id="register-domain-form" onSubmit={onSubmit} className="space-y-4">
          <FormField label="Domain" htmlFor="domain" required helper=".co.za / .org.za / .net.za / .web.za only">
            <Input
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={submitting}
              placeholder="example.co.za"
              autoComplete="off"
            />
          </FormField>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Period (years)" htmlFor="period_years" required>
              <select
                id="period_years"
                value={periodYears}
                onChange={(e) => setPeriodYears(Number(e.target.value))}
                disabled={submitting}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value={1}>1 year</option>
                <option value={2}>2 years</option>
              </select>
            </FormField>

            <FormField label="Bind to cPanel (optional)" htmlFor="bind_cpanel" helper="cPanel username of an existing hosting account">
              <Input
                id="bind_cpanel"
                value={bindToCpanel}
                onChange={(e) => setBindToCpanel(e.target.value)}
                disabled={submitting}
                placeholder="afrilin1"
              />
            </FormField>
          </div>

          <div className="rounded border border-border bg-muted/40 p-4">
            <h4 className="text-sm font-semibold text-foreground">Registrant (ZACR requirement)</h4>
            <p className="mt-1 mb-3 text-xs text-muted-foreground">
              Customer name + email stay as the legal registrant (they own the domain).
              Phone + address default to SightHost as agent of record — override below if the customer has their own.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Name" htmlFor="rd_name" required>
                <Input id="rd_name" value={registrant.name} onChange={(e) => updateRegistrant('name', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="Email" htmlFor="rd_email" required>
                <Input id="rd_email" type="email" value={registrant.email} onChange={(e) => updateRegistrant('email', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="Phone" htmlFor="rd_phone" required helper="+27.123456789 format">
                <Input id="rd_phone" value={registrant.contactNumber} onChange={(e) => updateRegistrant('contactNumber', e.target.value)} disabled={submitting} placeholder="+27.821234567" />
              </FormField>
              <FormField label="Organisation (optional)" htmlFor="rd_org">
                <Input id="rd_org" value={registrant.organisation ?? ''} onChange={(e) => updateRegistrant('organisation', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="Address line 1" htmlFor="rd_addr1" required>
                <Input id="rd_addr1" value={registrant.address1} onChange={(e) => updateRegistrant('address1', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="City" htmlFor="rd_city" required>
                <Input id="rd_city" value={registrant.city} onChange={(e) => updateRegistrant('city', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="Province" htmlFor="rd_province" required>
                <Input id="rd_province" value={registrant.province} onChange={(e) => updateRegistrant('province', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="Postal code" htmlFor="rd_postal" required>
                <Input id="rd_postal" value={registrant.postalCode} onChange={(e) => updateRegistrant('postalCode', e.target.value)} disabled={submitting} />
              </FormField>
              <FormField label="Country" htmlFor="rd_country" required helper="ISO alpha-2 (ZA)">
                <Input id="rd_country" value={registrant.country} onChange={(e) => updateRegistrant('country', e.target.value.toUpperCase().slice(0, 2))} disabled={submitting} maxLength={2} />
              </FormField>
            </div>
          </div>

          {error && <NotificationCard tone="danger" title="Could not register domain" body={error} />}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" form="register-domain-form" loading={submitting} disabled={submitting}>
          {submitting ? 'Issuing…' : 'Issue invoice + register'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
